import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Calendar, Package, BookOpen, UserCircle2, Sparkles } from "lucide-react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";

const FMT = { ebook: "Ebook", physical: "Physical", both: "Ebook + Physical" };

export default function CampaignDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [open, setOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [pref, setPref] = useState("ebook");
  const [submitting, setSubmitting] = useState(false);

  const load = () =>
    api.get(`/campaigns/${id}`).then(({ data }) => {
      setC(data);
      const initial = data.arc_format === "physical" ? "physical" : "ebook";
      setPref(initial);
    }).catch((e) => toast.error(apiError(e)));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const apply = async () => {
    if (!user) { nav("/login"); return; }
    if (user.role !== "reader") { toast.error("Only readers can apply"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/campaigns/${id}/apply`, { comment, preferred_format: pref });
      if (data.auto_accepted) toast.success("You were auto-accepted! Check your dashboard.");
      else toast.success("Application submitted — you’ll hear back soon.");
      setOpen(false);
      setComment("");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSubmitting(false); }
  };

  if (!c) return <div className="max-w-4xl mx-auto px-4 py-16 text-slate-500">Loading…</div>;

  const cover = c.cover_url || "https://images.unsplash.com/photo-1620233977581-509e4c6b0636?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";
  const deadline = c.review_deadline ? new Date(c.review_deadline).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }) : "TBD";

  const formatOptions = c.arc_format === "both" ? ["ebook", "physical"] : [c.arc_format];

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
        <div className="md:col-span-2">
          <div className="paper-card overflow-hidden">
            <img src={cover} alt={c.title} className="aspect-[3/4] w-full object-cover" data-testid="detail-cover-img" />
          </div>
        </div>
        <div className="md:col-span-3">
          <div className="overline mb-2">{c.genre}</div>
          <h1 className="font-serif text-4xl tracking-tight leading-tight" data-testid="detail-title">{c.title}</h1>
          <p className="text-slate-600 mt-2">by <span className="font-medium text-slate-900">{c.pen_name}</span> · <Link to={`/authors/${c.author_id}`} className="text-sm underline underline-offset-2 hover:opacity-80" data-testid="detail-author-link">{c.author_display_name}</Link></p>

          <div className="flex flex-wrap gap-2 mt-4">
            <Badge className="border bg-amber-50 text-amber-800 border-amber-200"><BookOpen size={12} className="mr-1" />{FMT[c.arc_format]}</Badge>
            <Badge variant="outline"><Package size={12} className="mr-1" />{c.copies_available} copies</Badge>
            <Badge variant="outline"><Calendar size={12} className="mr-1" />Review by {deadline}</Badge>
            {c.author_auto_accept_enabled && <Badge className="status-auto border"><Sparkles size={12} className="mr-1" />Auto-Accept Enabled</Badge>}
          </div>

          <section className="mt-8">
            <div className="overline mb-2">Synopsis</div>
            <p className="text-slate-700 leading-relaxed whitespace-pre-wrap" data-testid="detail-synopsis">{c.synopsis}</p>
          </section>

          {c.requirements && (
            <section className="mt-6">
              <div className="overline mb-2">Reviewer requirements</div>
              <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{c.requirements}</p>
            </section>
          )}

          <div className="mt-8 flex items-center gap-3">
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button
                  size="lg"
                  className="btn-press bg-[color:var(--oxford)] hover:bg-[color:var(--oxford-hover)] text-white rounded-full px-6"
                  onClick={() => { if (!user) nav("/login"); }}
                  data-testid="detail-apply-btn"
                >
                  {user?.role === "author" ? "Author view" : "Apply for this ARC"}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-serif text-2xl">Apply for “{c.title}”</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Preferred format</Label>
                    <RadioGroup value={pref} onValueChange={setPref} className="flex gap-6 mt-2">
                      {formatOptions.map((opt) => (
                        <div key={opt} className="flex items-center gap-2">
                          <RadioGroupItem value={opt} id={`fmt-${opt}`} data-testid={`apply-fmt-${opt}`} />
                          <Label htmlFor={`fmt-${opt}`} className="capitalize">{opt}</Label>
                        </div>
                      ))}
                    </RadioGroup>
                  </div>
                  <div>
                    <Label htmlFor="apply-comment">Note to the author (optional)</Label>
                    <Textarea
                      id="apply-comment"
                      placeholder="Tell the author why you'd love to review this book…"
                      rows={5}
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      data-testid="apply-comment-input"
                    />
                  </div>
                  {c.author_auto_accept_enabled && user?.auto_accept_agreement && (
                    <div className="text-xs text-indigo-800 bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                      <Sparkles size={14} className="inline mr-1" /> You’ll be auto-accepted based on your agreement with this author.
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="ghost" onClick={() => setOpen(false)} data-testid="apply-cancel-btn">Cancel</Button>
                  <Button
                    disabled={submitting}
                    onClick={apply}
                    className="btn-press bg-[color:var(--oxford)] hover:bg-[color:var(--oxford-hover)] text-white"
                    data-testid="apply-submit-btn"
                  >
                    {submitting ? "Submitting…" : "Submit application"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <div className="text-sm text-slate-500 flex items-center gap-2">
              <UserCircle2 size={16} /> {c.applications_count} readers applied
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
